import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI } from '../../utils/api';
import '../../styles/AdminDashboard.scss';
import { 
  FaComments, FaClipboardList, FaEnvelope, FaFolderOpen, 
  FaFire, FaChevronRight,
  FaArrowUp, FaArrowDown, FaCalendarAlt, FaTools, FaBriefcase,
  FaGraduationCap, FaCode, FaBookOpen, FaLaptopCode
} from 'react-icons/fa';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    contacts: 0,
    applications: 0,
    newsletter: 0,
    projects: 0,
    mbaProjects: 0,
    softwareProjects: 0,
    totalSales: 0,
    totalRevenue: 0,
    mbaEnquiries: 0,
    softwareEnquiries: 0,
    topSelling: [],
    topEnquiriesAll: [],
    topEnquiries: {
      software: [],
      mba: []
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await analyticsAPI.getSummary();
      const result = response.data;

      if (result.success) {
        const { summary, top_selling, top_enquiries_all, top_enquiries } = result.data;
        setStats({
          contacts: summary.contacts_count || 0,
          applications: summary.applications_count || 0,
          newsletter: summary.subscribers_count || 0,
          projects: summary.projects_count || 0,
          mbaProjects: summary.mba_projects_count || 0,
          softwareProjects: summary.software_projects_count || 0,
          totalSales: summary.total_sales || 0,
          totalRevenue: summary.total_revenue || 0,
          mbaEnquiries: summary.mba_enquiries_count || 0,
          softwareEnquiries: summary.software_enquiries_count || 0,
          topSelling: top_selling || [],
          topEnquiriesAll: top_enquiries_all || [],
          topEnquiries: top_enquiries || { software: [], mba: [] }
        });
      } else {
        throw new Error(result.error || 'Backend failed to process analytics');
      }
    } catch (err) {
      setError('Failed to load statistics.');
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, trend }) => (
    <div className={`stat-card-premium ${color}`}>
      <div className="card-inner">
        <div className="stat-info">
          <span className="stat-label">{title}</span>
          <h2 className="stat-value">{loading ? '...' : value}</h2>
          {trend && (
            <div className={`stat-trend ${trend > 0 ? 'up' : 'down'}`}>
              {trend > 0 ? <FaArrowUp /> : <FaArrowDown />}
              <span>{Math.abs(trend)}%</span>
              <span className="trend-text">since last month</span>
            </div>
          )}
        </div>
        <div className="stat-icon-wrapper">
          {icon}
        </div>
      </div>
      <div className="card-decoration"></div>
    </div>
  );

  return (
    <div className="admin-dashboard-page">
      <div className="dashboard-header">
        <div className="header-info">
          <h1>Dashboard Overview</h1>
          <p><FaCalendarAlt /> Welcome back! Here's what's happening today.</p>
        </div>
        <div className="header-actions">
          <button className="btn-refresh" onClick={fetchStats}>Refresh Data</button>
        </div>
      </div>

      {error && <div className="error-message-box">{error}</div>}

      <div className="premium-stats-grid">
        <StatCard
          title="Total Messages"
          value={stats.contacts}
          icon={<FaComments />}
          color="blue"
          trend={12}
        />
        <StatCard
          title="Job Applications"
          value={stats.applications}
          icon={<FaClipboardList />}
          color="green"
          trend={5}
        />
        <StatCard
          title="Subscribers"
          value={stats.newsletter}
          icon={<FaEnvelope />}
          color="purple"
          trend={-2}
        />
        <StatCard
          title="Total Projects"
          value={stats.projects}
          icon={<FaFolderOpen />}
          color="orange"
          trend={8}
        />
        <StatCard
          title="Software Total Projects"
          value={stats.softwareProjects}
          icon={<FaLaptopCode />}
          color="indigo"
        />
        <StatCard
          title="MBA Total Projects"
          value={stats.mbaProjects}
          icon={<FaBookOpen />}
          color="pink"
        />
        <StatCard
          title="Software Enquiries"
          value={stats.softwareEnquiries}
          icon={<FaCode />}
          color="cyan"
          trend={18}
        />
        <StatCard
          title="MBA Enquiries"
          value={stats.mbaEnquiries}
          icon={<FaGraduationCap />}
          color="red"
          trend={24}
        />
      </div>

      <div className="dashboard-grid-layout">
        <div className="admin-card interactive-list-card">
          <div className="card-header">
            <h3><FaFire color="#ff4d4d" /> Top Enquired Projects</h3>
            <button className="view-all-btn" onClick={() => navigate('/admin/enquiries')}>View All <FaChevronRight /></button>
          </div>
          
          <div className="interactive-list">
            {loading ? (
              <div className="skeleton-list">
                {[1,2,3,4,5].map(i => <div key={i} className="skeleton-item" />)}
              </div>
            ) : stats.topEnquiriesAll.length > 0 ? (
              stats.topEnquiriesAll.map((p, index) => {
                const isMba = p.category && p.category.startsWith('MBA -');
                const targetRoute = isMba ? '/admin/mba-enquiries' : '/admin/software-enquiries';
                return (
                  <div 
                    key={p.id || index} 
                    className="interactive-project-row"
                    onClick={() => navigate(`${targetRoute}?search=${encodeURIComponent(p.title)}`)}
                  >
                    <div className="project-rank">0{index + 1}</div>
                    <div className="project-main-info">
                      <span className="project-name">{p.title}</span>
                      <span className="project-cat">{p.category}</span>
                    </div>
                    <div className="project-stats">
                      <div className="stat-group">
                        <span className="label">Enquiries</span>
                        <span className="value">{p.enquiries_count}</span>
                      </div>
                    </div>
                    <div className="project-action">
                      <FaChevronRight />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="empty-state-list">
                <p>No enquiries recorded yet.</p>
              </div>
            )}
          </div>
        </div>

        <div className="admin-card quick-actions-card">
          <h3>Quick Actions</h3>
          <div className="quick-actions-grid">
            <button className="action-item" onClick={() => navigate('/admin/projects')}>
              <div className="action-icon"><FaFolderOpen /></div>
              <span>Add Project</span>
            </button>
            <button className="action-item" onClick={() => navigate('/admin/services')}>
              <div className="action-icon"><FaTools /></div>
              <span>CMS Update</span>
            </button>
            <button className="action-item" onClick={() => navigate('/admin/jobs')}>
              <div className="action-icon"><FaBriefcase /></div>
              <span>Post Job</span>
            </button>
            <button className="action-item" onClick={() => navigate('/admin/contacts')}>
              <div className="action-icon"><FaComments /></div>
              <span>Messages</span>
            </button>
          </div>
        </div>
      </div>

      <div className="dashboard-grid-layout enquiries-grid">
        <div className="admin-card interactive-list-card">
          <div className="card-header">
            <h3><FaCode color="#2cd9ff" /> Top Software Project Enquiries</h3>
            <button className="view-all-btn" onClick={() => navigate('/admin/software-enquiries')}>View All <FaChevronRight /></button>
          </div>
          
          <div className="interactive-list">
            {loading ? (
              <div className="skeleton-list">
                {[1,2,3,4,5].map(i => <div key={i} className="skeleton-item" />)}
              </div>
            ) : stats.topEnquiries?.software?.length > 0 ? (
              stats.topEnquiries.software.map((p, index) => (
                <div 
                  key={p.project_id || index} 
                  className="interactive-project-row"
                  onClick={() => navigate(`/admin/software-enquiries?search=${encodeURIComponent(p.project_title)}`)}
                >
                  <div className="project-rank">0{index + 1}</div>
                  <div className="project-main-info">
                    <span className="project-name">{p.project_title}</span>
                    <span className="project-cat">Software Project</span>
                  </div>
                  <div className="project-stats">
                    <div className="stat-group">
                      <span className="label">Enquiries</span>
                      <span className="value">{p.enquiries_count}</span>
                    </div>
                  </div>
                  <div className="project-action">
                    <FaChevronRight />
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state-list">
                <p>No enquiries recorded yet.</p>
              </div>
            )}
          </div>
        </div>

        <div className="admin-card interactive-list-card">
          <div className="card-header">
            <h3><FaGraduationCap color="#ee5d50" /> Top MBA Project Enquiries</h3>
            <button className="view-all-btn" onClick={() => navigate('/admin/mba-enquiries')}>View All <FaChevronRight /></button>
          </div>
          
          <div className="interactive-list">
            {loading ? (
              <div className="skeleton-list">
                {[1,2,3,4,5].map(i => <div key={i} className="skeleton-item" />)}
              </div>
            ) : stats.topEnquiries?.mba?.length > 0 ? (
              stats.topEnquiries.mba.map((p, index) => (
                <div 
                  key={p.project_id || index} 
                  className="interactive-project-row"
                  onClick={() => navigate(`/admin/mba-enquiries?search=${encodeURIComponent(p.project_title)}`)}
                >
                  <div className="project-rank">0{index + 1}</div>
                  <div className="project-main-info">
                    <span className="project-name">{p.project_title}</span>
                    <span className="project-cat">MBA Project</span>
                  </div>
                  <div className="project-stats">
                    <div className="stat-group">
                      <span className="label">Enquiries</span>
                      <span className="value">{p.enquiries_count}</span>
                    </div>
                  </div>
                  <div className="project-action">
                    <FaChevronRight />
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state-list">
                <p>No enquiries recorded yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
